/**
 * Phase 19 + redesign (PM 2026-06-12): `/#/leaderboard` page.
 *
 * Layout (canonical, per Bowz spec):
 *   [ Title + back arrow ]
 *   [ Segmented pill: Daily | Monthly ]
 *   [ Game picker dropdown: "All Puzzles" + 14 game options ]
 *   [ Three-column rows: rank | pfp + nym | score ]
 *   [ If user is outside top 10: separator + user's own row ]
 *
 * Row tinting:
 *   - Non-self #1/#2/#3: gold/silver/bronze, transparent
 *   - Self row at #1/#2/#3: gold/silver/bronze, SOLID
 *   - Self row outside top 3: blue, SOLID
 *   - All other rows: default
 *
 * Worker API:
 *   GET /api/leaderboard/daily?utcDate=YYYY-MM-DD&offset=0&limit=50[&gameId=<id>]
 *   GET /api/leaderboard/30day?offset=0&limit=50[&gameId=<id>]
 *   GET /api/leaderboard/me?utcDate=YYYY-MM-DD
 *
 * The `gameId` query param is sent when the picker is not "all"; the worker
 * may not yet honor it (Wave 2G) - in that case the unfiltered board is
 * returned and we paint it as a graceful no-op.
 */

import { apiJson } from '../lib/api';
import { loadAuth } from '../lib/session';
import {
  fetchProfile,
  isProfileMissCached,
  loadCachedProfile,
  type NostrProfile,
} from '../lib/profile';
import { utcDateString } from '@nostr-gives/components';
import { GAME_IDS, type GameId } from '@nostr-gives/shared';

type Tab = 'daily' | 'monthly';
type GamePick = 'all' | GameId;

interface LeaderboardRow {
  user_pubkey: string;
  total_points: number;
  games_played: number;
  total_time_ms?: number;
  days_played?: number;
  rank: number;
}

interface BoardResponse {
  rows: LeaderboardRow[];
  total: number;
  computedAt?: number;
  entries?: LeaderboardRow[];
  refreshedAt?: number;
}

interface MeResponse {
  dailyRank: number | null;
  dailyTotal: number;
  topNeighbours?: LeaderboardRow[];
  daily?: { rank: number | null; totalPoints: number; gamesPlayed: number; date?: string };
  last30?: { rank: number | null; totalPoints: number; gamesPlayed: number };
}

const PAGE_LIMIT = 50;
const PFP_CONCURRENCY = 5;
const INFINITE_SCROLL_CAP = 1000;

/**
 * Friendly labels for the game picker dropdown. Order mirrors the spec.
 * `nerdle` is the canonical id; the player-facing name is "Equate".
 */
const GAME_LABELS: Record<GameId, string> = {
  connections: 'Connections',
  worldle: 'Worldle',
  flagle: 'Flagle',
  '2048': '2048',
  nerdle: 'Equate',
  'spelling-bee': 'Spelling Bee',
  'chess-puzzle': 'Chess Puzzle',
  heardle: 'Heardle',
  minesweeper: 'Minesweeper',
  connect4: 'Connect4',
  nonogram: 'Nonogram',
  longword: 'LongWord',
  mathdoku: 'MathDoku',
  compound: 'Compound',
  slitherlink: 'Slitherlink',
  battleships: 'Battleships',
  echo: 'Echo',
  acrostic: 'Acrostic',
  'letter-boxed': 'Letter Boxed',
  'word-ladder': 'Word Ladder',
  'cross-number': 'Cross Number',
  'foldle': 'Foldle',
  'akari': 'Akari',
  'kakuro': 'Kakuro',
  leword: 'LeWord',
};

// Extra game ids that are catalogued + ship a tile but are not yet part of
// the canonical `GAME_IDS` union in @nostr-gives/shared. Empty after Phase
// 21 wired 2048 into the union; kept as a forward hook for the next game
// that lands in the catalogue before its scoring registration ships.
const EXTRA_GAME_LABELS: Record<string, string> = {};

interface PageState {
  tab: Tab;
  gameId: GamePick;
  rows: LeaderboardRow[];
  total: number;
  loading: boolean;
  fetchError: string | null;
  me: MeResponse | null;
  host: HTMLElement | null;
  nextOffset: number;
  exhausted: boolean;
  appendInFlight: boolean;
  appendError: string | null;
  fetchToken: number;
  observer: IntersectionObserver | null;
}

const state: PageState = {
  tab: 'daily',
  gameId: 'all',
  rows: [],
  total: 0,
  loading: false,
  fetchError: null,
  me: null,
  host: null,
  nextOffset: 0,
  exhausted: false,
  appendInFlight: false,
  appendError: null,
  fetchToken: 0,
  observer: null,
};

const pfpInFlight = new Set<string>();

function deriveStub(pubkey: string): string {
  if (pubkey.startsWith('stub_')) return pubkey.slice(5, 7).toUpperCase();
  if (pubkey.startsWith('anon_')) return pubkey.slice(5, 7).toUpperCase();
  return pubkey.slice(0, 2).toUpperCase();
}

function npubShort(pubkey: string): string {
  const head = pubkey.slice(0, 8);
  const tail = pubkey.slice(-4);
  return `npub1${head}...${tail}`;
}

function displayName(pubkey: string, profile: NostrProfile | null): string {
  if (profile?.display_name && profile.display_name.length > 0) return profile.display_name;
  if (profile?.name && profile.name.length > 0) return profile.name;
  return npubShort(pubkey);
}

function stubBackground(pubkey: string): string {
  let hash = 0;
  const slice = pubkey.slice(0, 8);
  for (let i = 0; i < slice.length; i++) {
    hash = (hash * 31 + slice.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 65% 55%)`;
}

function renderAvatar(pubkey: string, profile: NostrProfile | null): HTMLElement {
  const slot = document.createElement('span');
  slot.className = 'lb-avatar';
  slot.style.background = stubBackground(pubkey);
  if (profile?.picture) {
    const img = document.createElement('img');
    img.className = 'lb-avatar-img';
    img.src = profile.picture;
    img.alt = '';
    img.width = 36;
    img.height = 36;
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';
    img.loading = 'lazy';
    img.onerror = (): void => {
      img.replaceWith(stubInitials(pubkey));
    };
    slot.appendChild(img);
  } else {
    slot.appendChild(stubInitials(pubkey));
  }
  return slot;
}

function stubInitials(pubkey: string): HTMLElement {
  const span = document.createElement('span');
  span.className = 'lb-avatar-initials';
  span.textContent = deriveStub(pubkey);
  return span;
}

async function hydrateProfiles(rows: LeaderboardRow[]): Promise<void> {
  const queue = rows
    .map((r) => r.user_pubkey)
    .filter((pk) => !!pk && !pk.startsWith('anon_') && !pk.startsWith('stub_'))
    .filter((pk) => !loadCachedProfile(pk))
    // Pubkeys without a kind 0 anywhere on our READ_PROFILE_RELAYS are
    // cached as a negative for 1h - don't refetch them on every paint
    // or every infinite-scroll page. Cache is in-memory + localStorage.
    .filter((pk) => !isProfileMissCached(pk))
    .filter((pk) => !pfpInFlight.has(pk));
  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < queue.length) {
      const pk = queue[cursor++];
      if (!pk) continue;
      if (pfpInFlight.has(pk)) continue;
      pfpInFlight.add(pk);
      try {
        const profile = await fetchProfile(pk);
        if (profile) refreshRowsForPubkey(pk, profile);
      } catch {
        // ignore
      } finally {
        pfpInFlight.delete(pk);
      }
    }
  }
  const workers: Promise<void>[] = [];
  for (let i = 0; i < PFP_CONCURRENCY; i++) workers.push(worker());
  await Promise.all(workers);
}

function refreshRowsForPubkey(pubkey: string, profile: NostrProfile): void {
  if (!state.host) return;
  const nodes = state.host.querySelectorAll<HTMLElement>(`[data-pubkey="${pubkey}"]`);
  nodes.forEach((node) => {
    const nameEl = node.querySelector<HTMLElement>('.lb-nym');
    if (nameEl && !node.dataset.selfLabel) nameEl.textContent = displayName(pubkey, profile);
    const avatarEl = node.querySelector<HTMLElement>('.lb-avatar');
    if (avatarEl) {
      avatarEl.replaceWith(renderAvatar(pubkey, profile));
    }
  });
}

/**
 * Three-column row: rank | pfp + nym | score.
 * `data-rank` and `data-self` drive the tier-tint CSS.
 */
function renderRow(row: LeaderboardRow, isSelf: boolean): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lb-row';
  wrap.dataset.pubkey = row.user_pubkey;
  wrap.dataset.rank = String(row.rank);
  if (isSelf) {
    wrap.dataset.self = 'true';
    wrap.dataset.selfLabel = '1';
  }

  const rankCol = document.createElement('span');
  rankCol.className = 'lb-rank';
  rankCol.textContent = `#${row.rank}`;
  wrap.appendChild(rankCol);

  const identity = document.createElement('span');
  identity.className = 'lb-identity';
  const profile = loadCachedProfile(row.user_pubkey);
  identity.appendChild(renderAvatar(row.user_pubkey, profile));
  const nym = document.createElement('span');
  nym.className = 'lb-nym';
  nym.textContent = isSelf ? 'You' : displayName(row.user_pubkey, profile);
  identity.appendChild(nym);
  wrap.appendChild(identity);

  const score = document.createElement('span');
  score.className = 'lb-score';
  score.textContent = row.total_points.toLocaleString('en-US');
  wrap.appendChild(score);

  return wrap;
}

/**
 * Synthesise the self row when the player is outside the top 10. Uses the
 * `me` response for points + rank; pubkey + profile come from auth.
 */
function renderSelfRowOutsideTop10(
  me: MeResponse,
  auth: { pubkey: string },
): HTMLElement | null {
  const rank = me.dailyRank ?? me.daily?.rank ?? null;
  const points = me.dailyTotal ?? me.daily?.totalPoints ?? 0;
  if (rank === null) return null;
  const row: LeaderboardRow = {
    user_pubkey: auth.pubkey,
    total_points: points,
    games_played: 0,
    rank,
  };
  return renderRow(row, true);
}

function renderSignInBanner(): HTMLElement {
  const card = document.createElement('div');
  card.className = 'lb-signin-banner';

  const icon = document.createElement('div');
  icon.className = 'lb-signin-icon';
  icon.innerHTML = `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15"/><circle cx="12" cy="17" r="5"/></svg>`;
  card.appendChild(icon);

  const h2 = document.createElement('h2');
  h2.className = 'lb-signin-title';
  h2.textContent = 'Sign in to compete';
  card.appendChild(h2);

  const p = document.createElement('p');
  p.className = 'lb-signin-body';
  p.textContent = 'Track your daily rank + 30-day total. Anonymous play stays local.';
  card.appendChild(p);

  const cta = document.createElement('button');
  cta.type = 'button';
  cta.className = 'lb-signin-cta';
  cta.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg><span>Sign in with Nostr</span>`;
  cta.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('gives:open-login'));
  });
  card.appendChild(cta);

  return card;
}

/**
 * Worker returns rows in camelCase (`userPubkey`, `totalPoints`); this
 * page renders the snake_case shape (`user_pubkey`, `total_points`). Map
 * either input shape onto the canonical shape used downstream.
 */
function normaliseRow(r: unknown): LeaderboardRow | null {
  if (!r || typeof r !== 'object') return null;
  const o = r as Record<string, unknown>;
  const pubkey = (o.user_pubkey ?? o.userPubkey) as string | undefined;
  if (typeof pubkey !== 'string' || pubkey.length === 0) return null;
  const totalPoints = Number((o.total_points ?? o.totalPoints ?? 0));
  const gamesPlayed = Number((o.games_played ?? o.gamesPlayed ?? 0));
  const totalTime = Number((o.total_time_ms ?? o.totalTimeMs ?? 0));
  const daysPlayed = (o.days_played ?? o.daysPlayed) as number | undefined;
  const rank = Number((o.rank ?? 0));
  const out: LeaderboardRow = {
    user_pubkey: pubkey,
    total_points: Number.isFinite(totalPoints) ? totalPoints : 0,
    games_played: Number.isFinite(gamesPlayed) ? gamesPlayed : 0,
    total_time_ms: Number.isFinite(totalTime) ? totalTime : 0,
    rank: Number.isFinite(rank) ? rank : 0,
  };
  if (typeof daysPlayed === 'number') out.days_played = daysPlayed;
  return out;
}

function pickRowsArray(resp: BoardResponse | null): LeaderboardRow[] {
  if (!resp) return [];
  const raw: unknown[] = Array.isArray(resp.rows)
    ? resp.rows
    : Array.isArray(resp.entries)
      ? resp.entries
      : [];
  const out: LeaderboardRow[] = [];
  for (const r of raw) {
    const norm = normaliseRow(r);
    if (norm) out.push(norm);
  }
  return out;
}

function withGameId(path: string): string {
  if (state.gameId === 'all') return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}gameId=${encodeURIComponent(state.gameId)}`;
}

async function fetchBoard(tab: Tab, offset: number = 0): Promise<BoardResponse | null> {
  const base = tab === 'daily'
    ? `/api/leaderboard/daily?utcDate=${utcDateString()}&offset=${offset}&limit=${PAGE_LIMIT}`
    : `/api/leaderboard/30day?offset=${offset}&limit=${PAGE_LIMIT}`;
  try {
    const { status, data } = await apiJson<BoardResponse>(withGameId(base));
    if (status === 200 && data) return data;
    return null;
  } catch {
    return null;
  }
}

async function fetchMe(tab: Tab): Promise<MeResponse | null> {
  const auth = loadAuth();
  if (!auth || !auth.signerType || auth.signerType === 'anon') return null;
  const base = tab === 'daily'
    ? `/api/leaderboard/me?utcDate=${utcDateString()}`
    : `/api/leaderboard/me`;
  try {
    const { status, data } = await apiJson<MeResponse>(withGameId(base));
    if (status === 200 && data) return data;
    return null;
  } catch {
    return null;
  }
}

/**
 * Segmented Daily / Monthly toggle pill - one wrapper, two buttons sharing
 * the same track. `aria-selected` drives the active style.
 */
function renderTabStrip(): HTMLElement {
  const strip = document.createElement('div');
  strip.className = 'lb-tabs';
  strip.setAttribute('role', 'tablist');

  const makeBtn = (key: Tab, label: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lb-tab';
    btn.dataset.tab = key;
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', state.tab === key ? 'true' : 'false');
    btn.textContent = label;
    btn.addEventListener('click', () => {
      if (state.tab === key) return;
      state.tab = key;
      resetPagination();
      void reload();
    });
    return btn;
  };

  strip.append(makeBtn('daily', 'Daily'), makeBtn('monthly', 'Monthly'));
  return strip;
}

/**
 * Game picker dropdown. Defaults to "All Puzzles"; changes re-fetch with a
 * `gameId` query parameter.
 */
function renderGamePicker(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lb-game-picker-wrap';

  const select = document.createElement('select');
  select.className = 'lb-game-picker';
  select.setAttribute('aria-label', 'Filter leaderboard by game');

  const all = document.createElement('option');
  all.value = 'all';
  all.textContent = 'All Puzzles';
  select.appendChild(all);

  for (const id of GAME_IDS) {
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = GAME_LABELS[id] ?? id;
    select.appendChild(opt);
  }
  for (const [id, label] of Object.entries(EXTRA_GAME_LABELS)) {
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = label;
    select.appendChild(opt);
  }
  select.value = state.gameId;

  select.addEventListener('change', () => {
    const val = select.value as GamePick;
    if (val === state.gameId) return;
    state.gameId = val;
    resetPagination();
    void reload();
  });

  const chevron = document.createElement('span');
  chevron.className = 'lb-game-picker-chevron';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;

  wrap.append(select, chevron);
  return wrap;
}

function renderEmptyState(): HTMLElement {
  const empty = document.createElement('div');
  empty.className = 'lb-empty';
  if (state.fetchError) {
    empty.textContent = 'Leaderboard is offline right now. Try again in a moment.';
  } else {
    empty.textContent = state.tab === 'daily'
      ? 'No one has scored yet today. Be the first.'
      : 'The 30-day leaderboard is still warming up.';
  }
  return empty;
}

/**
 * Separator line shown between the top-10 list and the user's pinned row
 * when the user is outside the top 10.
 */
function renderSeparator(): HTMLElement {
  const sep = document.createElement('div');
  sep.className = 'lb-separator';
  sep.setAttribute('aria-hidden', 'true');
  sep.innerHTML = '<span class="lb-separator-line"></span><span class="lb-separator-dots">···</span><span class="lb-separator-line"></span>';
  return sep;
}

function renderListSection(): HTMLElement {
  const list = document.createElement('section');
  list.className = 'lb-list';

  const auth = loadAuth();
  const isLoggedIn = Boolean(auth && auth.signerType && auth.signerType !== 'anon');

  if (state.rows.length === 0) {
    list.appendChild(renderEmptyState());
    return list;
  }

  const top10 = state.rows.slice(0, 10);
  const selfPubkey = auth?.pubkey ?? null;
  const selfInTop10 = selfPubkey
    ? top10.some((r) => r.user_pubkey === selfPubkey)
    : false;

  const inner = document.createElement('div');
  inner.className = 'lb-rows';
  list.appendChild(inner);

  // Per spec: render top 10 rows (or as many as the board returned).
  for (const row of top10) {
    const isSelf = row.user_pubkey === selfPubkey;
    inner.appendChild(renderRow(row, isSelf));
  }

  // If the user is signed in and outside the top 10, add separator + self
  // row pinned below. Works on both Daily and Monthly tabs.
  if (isLoggedIn && !selfInTop10 && state.me) {
    const selfRow = renderSelfRowOutsideTop10(state.me, auth as { pubkey: string });
    if (selfRow) {
      list.appendChild(renderSeparator());
      list.appendChild(selfRow);
    }
  }

  // Continue listing rows past the top 10 via infinite scroll. Skip the
  // pinned self-row from the inner list (it will appear in the scroll
  // body too as the user reaches their slot).
  if (state.rows.length > 10) {
    const tail = state.rows.slice(10);
    const tailWrap = document.createElement('div');
    tailWrap.className = 'lb-rows lb-rows-tail';
    for (const row of tail) {
      const isSelf = row.user_pubkey === selfPubkey;
      tailWrap.appendChild(renderRow(row, isSelf));
    }
    list.appendChild(tailWrap);
  }

  if (!state.exhausted) {
    list.appendChild(renderPaginationFooter());
  } else if (state.rows.length >= INFINITE_SCROLL_CAP) {
    const note = document.createElement('p');
    note.className = 'lb-more';
    note.textContent = `Showing the top ${INFINITE_SCROLL_CAP.toLocaleString('en-US')}.`;
    list.appendChild(note);
  }

  return list;
}

function renderPaginationFooter(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lb-pagination';

  if (state.appendError) {
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'lb-retry';
    retry.textContent = "Couldn't load more. Tap to retry.";
    retry.addEventListener('click', () => {
      state.appendError = null;
      void fetchMore();
    });
    wrap.appendChild(retry);
    return wrap;
  }

  if (state.appendInFlight) {
    wrap.appendChild(renderInlineSpinner());
    return wrap;
  }

  const sentinel = document.createElement('div');
  sentinel.className = 'lb-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  wrap.appendChild(sentinel);

  queueMicrotask(() => {
    clearObserver();
    if (typeof IntersectionObserver === 'undefined') return;
    state.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void fetchMore();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    state.observer.observe(sentinel);
  });

  return wrap;
}

function renderInlineSpinner(): HTMLElement {
  const span = document.createElement('span');
  span.className = 'lb-spinner';
  span.setAttribute('role', 'status');
  span.setAttribute('aria-label', 'Loading more players');
  span.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`;
  return span;
}

function clearObserver(): void {
  if (state.observer) {
    state.observer.disconnect();
    state.observer = null;
  }
}

function resetPagination(): void {
  state.rows = [];
  state.nextOffset = 0;
  state.exhausted = false;
  state.appendInFlight = false;
  state.appendError = null;
  clearObserver();
}

async function fetchMore(): Promise<void> {
  if (state.appendInFlight) return;
  if (state.exhausted) return;
  if (state.rows.length >= INFINITE_SCROLL_CAP) {
    state.exhausted = true;
    paint();
    return;
  }

  state.appendInFlight = true;
  state.appendError = null;
  const fetchTab = state.tab;
  const token = state.fetchToken;
  paint();

  const board = await fetchBoard(fetchTab, state.nextOffset);

  if (token !== state.fetchToken || fetchTab !== state.tab) return;

  if (!board) {
    state.appendInFlight = false;
    state.appendError = 'fetch-failed';
    paint();
    return;
  }

  const newRows = pickRowsArray(board);
  if (newRows.length === 0) {
    state.exhausted = true;
    state.appendInFlight = false;
    paint();
    return;
  }

  const seen = new Set(state.rows.map((r) => r.user_pubkey));
  for (const r of newRows) {
    if (!seen.has(r.user_pubkey)) state.rows.push(r);
  }

  state.nextOffset += PAGE_LIMIT;
  if (newRows.length < PAGE_LIMIT) state.exhausted = true;
  if (state.rows.length >= INFINITE_SCROLL_CAP) state.exhausted = true;
  if (typeof board.total === 'number' && board.total > 0) {
    state.total = board.total;
  }
  state.appendInFlight = false;
  paint();
  void hydrateProfiles(newRows);
}

function renderHeader(): HTMLElement {
  const header = document.createElement('header');
  header.className = 'lb-header';

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'lb-back';
  back.setAttribute('aria-label', 'Back to home');
  back.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`;
  back.addEventListener('click', () => {
    window.location.hash = '#/';
  });
  header.appendChild(back);

  const title = document.createElement('h1');
  title.className = 'lb-title';
  title.textContent = 'Leaderboard';
  header.appendChild(title);

  const spacer = document.createElement('div');
  spacer.className = 'lb-back lb-back-spacer';
  spacer.setAttribute('aria-hidden', 'true');
  header.appendChild(spacer);

  return header;
}

function renderFooterNote(): HTMLElement {
  const note = document.createElement('p');
  note.className = 'lb-footer-note';
  if (state.tab === 'daily') {
    note.textContent = 'Updated continuously - resets 00:00 UTC';
  } else {
    note.textContent = 'Rolling 30-day window - solves only, timeouts excluded';
  }
  return note;
}

function paint(): void {
  if (!state.host) return;
  clearObserver();
  state.host.innerHTML = '';
  const page = document.createElement('section');
  page.className = 'lb-page';
  page.append(renderHeader(), renderTabStrip(), renderGamePicker());

  const auth = loadAuth();
  const isLoggedIn = Boolean(auth && auth.signerType && auth.signerType !== 'anon');
  if (!isLoggedIn) {
    page.appendChild(renderSignInBanner());
  }

  if (state.loading) {
    const spinner = document.createElement('div');
    spinner.className = 'lb-loading';
    spinner.textContent = 'Loading...';
    page.appendChild(spinner);
  } else {
    page.appendChild(renderListSection());
  }

  page.appendChild(renderFooterNote());
  state.host.appendChild(page);

  if (!isLoggedIn) {
    const viewOnly = document.createElement('p');
    viewOnly.className = 'lb-view-only';
    viewOnly.textContent = 'You can view the board without an account.';
    state.host.appendChild(viewOnly);
  }
}

async function reload(): Promise<void> {
  state.loading = true;
  state.fetchError = null;
  state.fetchToken += 1;
  const token = state.fetchToken;
  clearObserver();
  paint();
  const [board, me] = await Promise.all([fetchBoard(state.tab, 0), fetchMe(state.tab)]);
  if (token !== state.fetchToken) return;
  const rows = pickRowsArray(board);
  state.rows = rows;
  state.total = board?.total ?? rows.length;
  state.me = me;
  state.loading = false;
  state.nextOffset = rows.length;
  state.exhausted = rows.length < PAGE_LIMIT || rows.length >= INFINITE_SCROLL_CAP;
  state.appendInFlight = false;
  state.appendError = null;
  if (!board) state.fetchError = 'fetch-failed';
  paint();
  void hydrateProfiles(rows);
}

export function renderLeaderboard(host: HTMLElement, hash: string): void {
  state.host = host;
  // Hash format: `#/leaderboard?game=X&tab=daily`. Everything after the
  // first `?` is the URLSearchParams payload. Read `game` + `tab` and
  // override the page state so deep links from per-game rank pills land
  // on the right filter.
  const search = hash.split('?')[1] ?? '';
  if (search) {
    try {
      const params = new URLSearchParams(search);
      const gameVal = params.get('game');
      if (gameVal) {
        // Accept any string; the worker filter will return an empty board
        // for unknown game ids, which the UI already handles.
        state.gameId = gameVal as GamePick;
      }
      const tabVal = params.get('tab');
      if (tabVal === 'daily' || tabVal === 'monthly') {
        state.tab = tabVal;
      } else if (tabVal === '30day') {
        // Brief from PM 2026-06-13 uses `30day` as a synonym for the
        // monthly view. Map it through so deep links from the rank pill
        // work even though the internal Tab union is `daily | monthly`.
        state.tab = 'monthly';
      }
    } catch {
      // Bad query string -> fall back to existing state.
    }
  }
  paint();
  void reload();
}
